/**
 * Master Data Hook
 * Provides cached access to manufacturing master data
 *
 * @description
 * This hook provides access to:
 * - Raw material types
 * - Dung types
 * - QC grades
 * - Product types (hierarchical)
 *
 * All data is cached for 10 minutes to reduce API calls.
 *
 * @author SAUBHAGYA Development Team
 * @since Story 2-5
 */

import { useQuery } from '@tanstack/react-query';
import { manufacturingApi } from '@/services/manufacturing/manufacturingApi';

/**
 * Hook to fetch and cache master data
 * @returns Object containing raw material types, dung types, and loading state
 */
export function useMasterData() {
  const rawMaterialTypes = useQuery({
    queryKey: ['rawMaterialTypes'],
    queryFn: manufacturingApi.getRawMaterialTypes,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    retry: 2,
  });

  const dungTypes = useQuery({
    queryKey: ['dungTypes'],
    queryFn: manufacturingApi.getDungTypes,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    retry: 2,
  });

  const qcGrades = useQuery({
    queryKey: ['qcGrades'],
    queryFn: manufacturingApi.getQcGrades,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    retry: 2,
  });

  const productTypes = useQuery({
    queryKey: ['productTypes'],
    queryFn: manufacturingApi.getProductTypes,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    retry: 2,
  });

  return {
    rawMaterialTypes: rawMaterialTypes.data,
    dungTypes: dungTypes.data,
    qcGrades: qcGrades.data,
    productTypes: productTypes.data,
    isLoading:
      rawMaterialTypes.isLoading ||
      dungTypes.isLoading ||
      qcGrades.isLoading ||
      productTypes.isLoading,
    isError:
      rawMaterialTypes.isError ||
      dungTypes.isError ||
      qcGrades.isError ||
      productTypes.isError,
    error:
      rawMaterialTypes.error ||
      dungTypes.error ||
      qcGrades.error ||
      productTypes.error,
  };
}

/**
 * Hook to fetch only raw material types
 * @returns Query result for raw material types
 */
export function useRawMaterialTypes() {
  return useQuery({
    queryKey: ['rawMaterialTypes'],
    queryFn: manufacturingApi.getRawMaterialTypes,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch only dung types
 * @returns Query result for dung types
 */
export function useDungTypes() {
  return useQuery({
    queryKey: ['dungTypes'],
    queryFn: manufacturingApi.getDungTypes,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch only QC grades
 * @returns Query result for QC grades
 */
export function useQcGrades() {
  return useQuery({
    queryKey: ['qcGrades'],
    queryFn: manufacturingApi.getQcGrades,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch only product types
 * @deprecated Use useDungProducts() instead
 * @returns Query result for product types (hierarchical)
 */
export function useProductTypes() {
  return useQuery({
    queryKey: ['productTypes'],
    queryFn: manufacturingApi.getProductTypes,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch dung products for production batch creation
 * @returns Query result for dung products
 */
export function useDungProducts() {
  return useQuery({
    queryKey: ['dungProducts'],
    queryFn: manufacturingApi.getDungProducts,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
  });
}
